import { computed, Injectable, signal } from "@angular/core";
import { FirebaseService } from "./firebase.service";
import { StorageService } from "./storage.service";
import { LogService } from "./log.service";
import type { IEvaluateSubscription } from "../models";
import { IHttpLogin, StorageKey } from "../models";

import { environment } from "src/environments/environment";
import { LoginApiService } from "./api";
import {
  assertTenantGuid,
  assertTenantId,
  assertUserId,
  isGuid,
} from "../utils/helpers";
import { User } from "firebase/auth";

@Injectable({
  providedIn: "root",
})
export class UserService {
  /** The current or default tenantid for api */
  #tenantId = signal<number | null>(null);
  /** The current or default userid for api */
  #userId = signal<number | null>(null);
  /** Tenant GUID for page routing */
  tenantGuid = signal<string>("");
  /** Signal admin value - for subscribing to with computed */
  isAdmin = signal<boolean | null>(null);
  /** Signal admin value - for subscribing to with computed */
  #tier = signal<number | null>(null);
  /** Current tenant name */
  tenantName = signal<string>("DefectWise");
  /**
   * Boolean for whether defect limit reached
   */
  limitReached: boolean = false;
  /**
   * Current usage by user(s)
   */
  defectCount: number = 0;
  /**
   * Whether user is enabled or disabled by parent organisation
   * @deprecated Use the `isEnabled` signal instead
   */
  enabled: boolean = true;
  isEnabled = signal<boolean>(true);
  /**
   * Total number of defects that can be created
   */
  private defectLimit: number = 100;
  /**
   * Usage options for payment tiers
   * @deprecated use result from server where possible
   */
  private readonly usageOpts: number[] = [100, 2000, 100000];
  /** */
  private loadingTenant: boolean = false;
  private loadingAttempts: number = 0;

  /** @ignore */
  constructor(
    private readonly store: StorageService,
    private readonly log: LogService,
    private readonly loginApi: LoginApiService,
    private readonly fb: FirebaseService
  ) {}

  /**
   * Signal premium value
   *
   * change the UserService.#tier signal to update this
   *
   * - for subscribing to with computed
   */
  isPremium = computed(() => {
    const tier = this.#tier();
    if (tier === null) return null;
    return tier > 0;
  });

  enterprise = computed<boolean>(() => this.#tier() === 2);

  /** Has the user maxxed their defects */
  // defectLimitReach = computed<boolean>(
  //   () => this.defectCount >= this.defectLimit
  // );

  /**
   * Init payment variable for UI display
   * ```
   * tier: number; // pass in payment tier to update service
   * admin: boolean | undefined | null; // pass in whether user is tenant admin or not
   * enabled: boolean; // archived status of user
   * defectLimit: number;
   * defectCount: number;
   * ```
   */
  evaluateSubscription(data: IEvaluateSubscription): void {
    // console.log("Evaluating Subscription, TIER: " + tier)
    const newTier = data.tier || 0;
    this.#tier.update(() => newTier);

    const enabled = data.enabled !== undefined ? data.enabled : true;
    this.enabled = enabled;
    this.isEnabled.update(() => enabled);
    this.isAdmin.update(() => data.admin ?? false);

    this.defectCount = data.defectCount;
    this.defectLimit =
      data.defectLimit !== undefined
        ? data.defectLimit
        : this.usageOpts[newTier];

    this.limitReached = this.defectCount > this.defectLimit;

    if (!environment.production) {
      console.log(
        `%cSUBSCRIPTION: evaluateSubscription(): premium: ${this.isPremium()}, admin: ${this.isAdmin()}, enabled: ${enabled}, defectLimit: ${
          this.defectLimit
        }, defectCount: ${this.defectCount}`,
        "background-color:blue;color:white"
      );
    }

    return;
  }
  /**
   * Checks and gets the NORMAL type user IDs.
   *
   * @return {Promise<{ userId: number | null; tenantId: number | null; }>} General user IDs.
   */
  async checkAndGetUserIds(options?: { returnNullOnError: boolean }): Promise<{
    userId: number | null;
    tenantId: number | null;
  }> {
    const userId = this.#userId();
    const tenantId = this.#tenantId();
    if (userId && tenantId) {
      return { userId, tenantId };
    } else if (options?.returnNullOnError) {
      return { userId: null, tenantId: null };
    } else {
      await this.logout();
      throw new Error("Error determining user, log in again.");
    }
  }
  /**
   *
   */
  async getUsername(): Promise<string> {
    const value = await this.store.getString(StorageKey.username);
    return value || "";
  }
  /**
   * Runs before most / all api calls to ensure tenantid/userid are available
   * and app is showing correct status based on isPremium
   *
   * Give app 10 seconds to load premium
   * @returns {boolean} premium value
   */
  async awaitPremium(): Promise<boolean> {
    if (!environment.production) {
      console.log("Awaiting premium");
    }

    // if premium is not null, it is loaded and ok to use
    if (this.isPremium() !== null) {
      return this.isPremium() ?? false;
    }

    try {
      if (this.loadingTenant) {
        // already init'd loading tenant data, wait for existing response
        const secondsToWait = 10;
        let currentPremiumState = null;
        while (this.loadingAttempts < 10) {
          this.loadingAttempts++;
          currentPremiumState = this.isPremium();
          if (currentPremiumState !== null) {
            break;
          }
          await new Promise((resolve) =>
            setTimeout(resolve, secondsToWait * 100)
          );
        }

        this.loadingAttempts = 0;
        return this.isPremium() ?? false;
      } else {
        this.loadingTenant = true;

        let userId: number | null = null;
        let tenantId: number | null = null;

        // else, check if the user/tenant ids are already set from router
        const currentIdStat = await this.checkAndGetUserIds({
          returnNullOnError: true,
        });
        userId = currentIdStat.userId;
        tenantId = currentIdStat.tenantId;

        // else, init them from scratch
        if (!userId || !tenantId) {
          const initIdState = await this.initUserIds();
          userId = initIdState.userId;
          tenantId = initIdState.tenantId;
        }

        if (userId === null || tenantId === null) {
          throw new Error(
            "Unable to determine user, please log out then in again."
          );
        }

        const result = await this.loginApi.getTenantTier(userId, tenantId);

        if (result.success) {
          const { tier, isAdmin, archived, defectLimit, defectCount } =
            result.data;

          this.evaluateSubscription({
            tier,
            admin: isAdmin,
            enabled: !archived,
            defectLimit,
            defectCount,
          });

          return true;
        }

        this.loadingTenant = false;
      }
    } catch (ex) {
      console.error(ex);
    } finally {
      this.loadingTenant = false;
    }

    throw new Error("Unable to determine user, please log out then in again.");
  }
  /**
   *
   */
  async awaitAdmin(): Promise<boolean> {
    let currentAdminState = this.isAdmin();
    try {
      if (currentAdminState === null) {
        await this.awaitPremium();
        currentAdminState = this.isAdmin() ?? false;
      }
    } catch (e) {
      currentAdminState = false;
    }
    return currentAdminState;
  }
  /**
   * Get Firebase Auth Email
   * @returns
   */
  async getEmail(): Promise<string | null> {
    const user = await this.fb.getUser();
    return user?.email || null;
  }
  /**
   * Get Firebase Auth Email
   * @returns
   */
  async getCurrentUser(): Promise<User | null> {
    return this.fb.getUser();
  }
  /**
   * Get the token from firebase
   * @returns
   */
  async getUserToken(): Promise<string | null> {
    return this.fb.getToken();
  }
  /**
   * Returns basic user information for account page and saves it to device
   * @returns {IHttpLogin | null} User data if user is found, otherwise null
   * @since 1.2.0
   */
  async getLogin(): Promise<{
    success: boolean;
    message: string;
    user: IHttpLogin | null;
  }> {
    if (!environment.production) {
      console.log("getLogin(): /Login");
    }

    try {
      const response = await this.loginApi.userLogin();

      if (!response.data || response.success === false) {
        return { success: false, message: "Unknown Error - Login", user: null };
      }

      const { success, message, data } = response;
      const user: IHttpLogin | null = data;

      let defaultTenantGuid: string = "";
      for (const t of user.tenants) {
        if (t.isDefault || t.tenantId === user.tenantId) {
          defaultTenantGuid = t.tGuid;
          break;
        }
      }

      if (user) {
        await Promise.all([
          this.store.storeTenantKeys(user.tenants),
          this.store.set(StorageKey.defaultTenantGuid, defaultTenantGuid),
          this.store.set(StorageKey.userId, user.id),
          this.store.set(
            StorageKey.username,
            `${user.firstname} ${user.lastname}`.trim()
          ),
        ]);

        // Save archived state as `enabled`
        this.enabled = !user.archived;
        this.isEnabled.update(() => !user.archived);
        this.#tier.update(() => user.tier);
        this.#userId.update(() => user.id);
        this.#tenantId.update(() => user.tenantId);

        this.evaluateSubscription({
          tier: user.tier,
          admin: null,
          enabled: !user.archived,
          defectLimit: user.defectLimit,
          defectCount: user.defectCount,
        });

        const fireUser = await this.fb.getUser();
        if (fireUser !== null) {
          this.log.identifyUser({
            email: fireUser.email,
            id: fireUser.uid,
            paid: user.tier !== 0,
          });
        }
      }

      return Promise.resolve({ success, message, user });
    } catch (ex: any) {
      const loginError: string = `${ex} ${ex?.message}`;
      if (loginError.includes("Unable to retrieve login")) {
        return Promise.resolve({
          success: true,
          message: "Unable to retrieve login",
          user: null,
        });
      } else {
        return Promise.reject(ex ?? "Unknown Exception - Login");
      }
    }
  }

  /**
   * Log user out and reset UserService
   */
  async logout(): Promise<void> {
    await this.store.clear();
    await this.fb.signOut();
    this.tenantGuid.update(() => "");
    this.#tenantId.update(() => null);
    this.#userId.update(() => null);
    // delay the tier update to avoid UI elements from updating
    // during page transitions
    setTimeout(() => this.#tier.update(() => null), 100);
  }

  /**
   * Used for getting fallback tenant id
   *
   * - During initial login
   * - When routing to empty page
   *
   * @returns {string | null} tenant guid or null
   * @since 3.1.0
   */
  async getDefaultTenantGuid(): Promise<string | null> {
    const result = await this.store.getString(StorageKey.defaultTenantGuid);
    this.tenantGuid.update(() => result ?? "");
    return result;
  }

  /** Shortcut to store - called from popover on switch tenant */
  async setDefaultTenantGuid(defaultTenantGuid: string): Promise<void> {
    await this.store.set(StorageKey.defaultTenantGuid, defaultTenantGuid);
  }

  /**
   * Login - Token Guard
   *
   * Checks and gets the NORMAL type user IDs.
   *
   * @param {string} tenantGuidRoute the tenantid in the url route
   * @return {Promise<{ userId: number | null; tenantId: number | null; }>} General user IDs.
   * @since 3.1.0
   */
  async initUserIds(tenantGuidRoute?: string): Promise<{
    userId: number | null;
    tenantId: number | null;
    /** The current or default route */
    tenantGuid: string | null;
  }> {
    // Test the route guid.
    // if it is invalid, clear it to load default instead
    try {
      if (tenantGuidRoute) {
        assertTenantGuid(tenantGuidRoute);
      }
    } catch (ex) {
      tenantGuidRoute = undefined;
    }

    try {
      let tenantGuid = isGuid(tenantGuidRoute)
        ? tenantGuidRoute
        : await this.getDefaultTenantGuid();

      assertTenantGuid(tenantGuid);

      const res = await Promise.all([
        this.store.getNumber(StorageKey.userId),
        this.store.getTenantId(tenantGuid),
      ]);

      let userId = res[0];
      let tenantId = res[1];

      // user has no access to this tenant, change to default
      if (tenantGuidRoute !== undefined && tenantId == null) {
        tenantGuid = await this.getDefaultTenantGuid();
        assertTenantGuid(tenantGuid);

        tenantId = await this.store.getTenantId(tenantGuid);
      }

      assertUserId(userId);
      assertTenantId(tenantId);

      this.#userId.update(() => userId);
      this.#tenantId.update(() => tenantId);
      this.tenantGuid.update(() => tenantGuid);

      /** Tenant ids good to go, load admin,owner,tier options */
      await this.awaitPremium();

      return { userId, tenantId, tenantGuid };
    } catch (ex) {
      console.error(ex);
      return { userId: null, tenantId: null, tenantGuid: null };
    }
  }

  /**
   * Sets the tenant guid and resets the user service
   *
   * - Requires getTenant or getLogin
   *
   * Called from:
   * - app.component
   * - workspaces home menu
   * - checkout /billing screen
   *
   * @param tenantGuid
   *
   * @since 3.1.0
   */
  async setTenantIdOnRoute(tenantGuidRoute: string): Promise<void> {
    try {
      assertTenantGuid(tenantGuidRoute);

      const tenantId = await this.store.getTenantId(tenantGuidRoute);
      if (this.#tenantId() !== tenantId) {
        // reset for new tenant change
        this.#tier.update(() => null);
        this.isAdmin.update(() => null);
        this.#tenantId.update(() => tenantId);
        this.tenantGuid.update(() => tenantGuidRoute);
      }
    } catch (ex) {
      //
    }
  }
}
